import React, { useState, useRef, useCallback, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import {
  Card,
  CardContent,
} from '../components/common/Card';
import { Button } from '../components/common/Button';
import Layout from '../components/common/Layout';
import {
  ViewHorizontalIcon,
  CalendarIcon,
  ClockIcon,
  ListBulletIcon,
  Cross2Icon,
} from '@radix-ui/react-icons';
import { getCalendarActivities } from '../api/activity';
import { format } from 'date-fns';
import { convertToTimezone } from '../utils/timezone';
import {v4 as uuidv4} from 'uuid';

const EventPreview = ({ event, onClose }) => {
  if (!event) return null;

  const startTime = convertToTimezone(new Date(event.start));
  const endTime = convertToTimezone(new Date(event.end));

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/20" />
      <div 
        className="relative bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4 space-y-4"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: event.backgroundColor,
          border: `1px solid ${event.borderColor}`,
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <Cross2Icon className="w-4 h-4" />
        </button>

        <div className="space-y-2">
          <h3 
            className="text-lg font-semibold"
            style={{ color: event.textColor }}
          >
            {event.title}
          </h3>
          
          <div className="space-y-1 text-sm" style={{ color: event.textColor }}>
            <div className="flex items-center gap-2">
              <ClockIcon className="w-4 h-4" />
              <span>{format(startTime, 'MMM d, yyyy')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="ml-6">
                {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CalendarPage = () => {
  const [view, setView] = useState('dayGridMonth');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const calendarRef = useRef(null);
  const eventsCache = useRef(new Map());

  // Helper function to generate cache key
  const getCacheKey = (params) => {
    switch (params.filterType) {
      case 'MONTH':
        return `MONTH-${params.year}-${params.month}`;
      case 'WEEK':
        return `WEEK-${params.weekNumber}`;
      case 'DAY':
        return `DAY-${params.year}-${params.month}-${params.day}`;
      default:
        return null;
    }
  };

  // Elegant color palette for events
  const eventColors = [
    {
      backgroundColor: '#E6F3FF',
      textColor: '#1E40AF',
      borderColor: '#93C5FD'
    },
    {
      backgroundColor: '#F0FDF4',
      textColor: '#166534',
      borderColor: '#86EFAC'
    },
    {
      backgroundColor: '#FDF2F8',
      textColor: '#9D174D',
      borderColor: '#F9A8D4'
    },
    {
      backgroundColor: '#F5F3FF',
      textColor: '#5B21B6',
      borderColor: '#C4B5FD'
    },
    {
      backgroundColor: '#FEF3C7',
      textColor: '#92400E',
      borderColor: '#FCD34D'
    },
    {
      backgroundColor: '#ECFEFF',
      textColor: '#155E75',
      borderColor: '#67E8F9'
    }
  ];

  const fetchEvents = useCallback(async (start, end, type) => {
    try {
      let params = {};
      const startDate = new Date(start);
      
      switch (type) {
        case 'dayGridMonth': {
          // Get the calendar's current date which represents the month in view
          const currentDate = calendarRef.current.getApi().getDate();
          params = {
            filterType: 'MONTH',
            month: currentDate.getMonth() + 1, // Convert from 0-based to 1-based month
            year: currentDate.getFullYear()
          };
          break;
        }
        case 'timeGridWeek':
        case 'listWeek': {
          const weekNumber = getWeekNumber(startDate);
          params = {
            filterType: 'WEEK',
            weekNumber
          };
          break;
        }
        case 'timeGridDay': {
          params = {
            filterType: 'DAY',
            day: startDate.getDate(),
            month: startDate.getMonth() + 1, // Convert from 0-based to 1-based month
            year: startDate.getFullYear()
          };
          break;
        }
        default: {
          console.warn('Unhandled view type:', type);
          return;
        }
      }

      const cacheKey = getCacheKey(params);
      
      // Check if we have cached data
      if (cacheKey && eventsCache.current.has(cacheKey)) {
        const cachedEvents = eventsCache.current.get(cacheKey);
        setEvents(cachedEvents);
        return;
      }

      const activities = await getCalendarActivities(params);
      
      const transformedEvents = activities.map((activity, index) => {
        const colorIndex = index % eventColors.length;
        const colors = eventColors[colorIndex];

        return {
          id: uuidv4(),
          title: activity.name,
          start: convertToTimezone(activity.startTime),
          end: convertToTimezone(activity.endTime),
          allDay: false,
          backgroundColor: colors.backgroundColor,
          textColor: colors.textColor,
          borderColor: colors.borderColor,
          className: 'custom-calendar-event'
        };
      });

      // Cache the transformed events
      if (cacheKey) {
        eventsCache.current.set(cacheKey, transformedEvents);
      }

      setEvents(transformedEvents);
    } catch (error) {
      // TODO: Add proper error handling/notification
    }
  }, [eventColors]);

  // Helper function to get ISO week number
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const handleDatesSet = useCallback((arg) => {
    const { start, end, view } = arg;
    fetchEvents(start, end, view.type);
  }, [fetchEvents]);

  const handleViewChange = (newView) => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.changeView(newView);
    setView(newView);
  };

  const handleViewDidMount = (arg) => {
    setView(arg.view.type);
  };

  const handleEventClick = (clickInfo) => {
    setSelectedEvent(clickInfo.event);
  };

  const closeEventPreview = () => {
    setSelectedEvent(null);
  };

  // Add useEffect for initial data load
  useEffect(() => {
    // Small delay to ensure calendar is mounted and ref is available
    const timer = setTimeout(() => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const currentView = calendarApi.view;
        fetchEvents(currentView.activeStart, currentView.activeEnd, currentView.type);
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, [fetchEvents]); // Add fetchEvents to dependency array

  // Add cache cleanup on unmount
  useEffect(() => {
    return () => {
      eventsCache.current.clear();
    };
  }, []);

  const renderViewButton = (viewName, icon, label) => (
    <Button
      variant={view === viewName ? "default" : "outline"}
      size="sm"
      onClick={() => handleViewChange(viewName)}
      className="flex items-center gap-2"
    >
      {icon}
      <span className="hidden md:inline">{label}</span>
    </Button>
  );

  return (
    <Layout>
      <div className="space-y-7">
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Calendar</h1>
            <p className="text-muted-foreground">
              View and manage your activities schedule
            </p>
          </div>
          <div className="flex items-center gap-2">
            {renderViewButton('dayGridMonth', <CalendarIcon className="h-4 w-4" />, 'Month')}
            {renderViewButton('timeGridWeek', <ViewHorizontalIcon className="h-4 w-4" />, 'Week')}
            {renderViewButton('timeGridDay', <ClockIcon className="h-4 w-4" />, 'Day')}
            {renderViewButton('listWeek', <ListBulletIcon className="h-4 w-4" />, 'List')}
          </div>
        </div>

        {/* Calendar Card */}
        <Card className="border-0 shadow-none">
          <CardContent className="pt-0">
            <div className="calendar-container">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                initialView={view}
                viewDidMount={handleViewDidMount}
                headerToolbar={{
                  right: 'prev,next today',
                  left: 'title',
                  center: '',
                }}
                height="800px"
                events={events}
                editable={false}
                selectable={true}
                selectMirror={true}
                dayMaxEvents={true}
                weekends={true}
                firstDay={1}
                nowIndicator={true}
                slotMinTime="00:00:00"
                slotMaxTime="24:00:00"
                stickyHeaderDates={true}
                datesSet={handleDatesSet}
                eventClick={handleEventClick}
                className="modern-calendar"
                themeSystem='bootstrap5'
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Event Preview Modal */}
      {selectedEvent && (
        <EventPreview 
          event={selectedEvent}
          onClose={closeEventPreview}
        />
      )}

      {/* Add custom styles */}
      <style jsx global>{`
        .modern-calendar {
          --fc-border-color: hsl(var(--border) / 0.1);
          --fc-today-bg-color: hsl(var(--primary) / 0.05);
          --fc-neutral-bg-color: hsl(var(--background));
          --fc-list-event-hover-bg-color: hsl(var(--muted));
          --fc-theme-standard-border-color: hsl(var(--border) / 0.1);
          --fc-highlight-color: hsl(var(--primary) / 0.1);
        }

        .calendar-container .fc {
          font-family: var(--font-sans);
          border-radius: 0.75rem;
          overflow: hidden;
        }

        .calendar-container .fc-toolbar-title {
          font-size: 1.25rem;
          font-weight: 500;
          color: hsl(var(--foreground) / 0.8);
        }

        .calendar-container .fc-button {
          display: inline-flex;
          items-center: center;
          justify-content: center;
          border-radius: var(--radius);
          font-weight: 400;
          font-size: 0.875rem;
          line-height: 1.25rem;
          padding: 0.5rem 1rem;
          background-color: hsl(var(--muted));
          color: hsl(var(--muted-foreground));
          border: none;
          transition: all 0.2s;
          text-transform: capitalize;
        }

        .calendar-container .fc-button:focus {
          outline: none;
          box-shadow: none;
        }

        .calendar-container .fc-button:hover {
          background-color: hsl(var(--muted) / 0.8);
          color: hsl(var(--foreground));
        }

        .calendar-container .fc-button-active {
          background-color: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
        }

        .calendar-container .fc-button-active:hover {
          color: hsl(var(--primary-foreground));
        }

        .calendar-container .fc-day-today {
          background-color: hsl(var(--primary) / 0.05) !important;
        }

        .calendar-container .fc-event {
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 0.875rem;
          font-weight: 500;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          margin: 2px 0;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          cursor: pointer;
        }

        .calendar-container .fc-event:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }

        .calendar-container .fc-event-main {
          padding: 2px 0;
        }

        .calendar-container .fc-list-event {
          background: transparent !important;
        }

        .calendar-container .fc-list-event td {
          padding: 8px 14px;
        }

        .calendar-container .fc-list-event-dot {
          display: none;
        }

        .calendar-container .fc-list-event-title {
          font-weight: 500;
        }

        .calendar-container .custom-calendar-event {
          border-width: 1px;
          border-style: solid;
        }
        
        /* Additional styles for better time display */
        .calendar-container .fc-event-time {
          font-size: 0.75rem;
          opacity: 0.9;
          font-weight: 400;
        }

        /* Add smooth transition for modal */
        .fixed {
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </Layout>
  );
};

export default CalendarPage; 